
function getUniqueMoleculesFromEnzyme(enzymesData) {
    const resProducts = [];
    const resSubstrates = [];
    const uniqueProducts = new Set();
    const uniqueSubstrates = new Set();
    const productCounts = {};
    const substrateCounts = {};

    enzymesData.forEach(enzyme => {
        enzyme.reactions.forEach(reaction => {
            reaction.substrates.forEach(substrate => {
                if (!uniqueSubstrates.has(substrate.marts)) {
                    uniqueSubstrates.add(substrate.marts);
                    resSubstrates.push(substrate);
                }
                substrateCounts[substrate.marts] = (substrateCounts[substrate.marts] || 0) + 1;
            });
            if (!uniqueProducts.has(reaction.product.marts)) {
                uniqueProducts.add(reaction.product.marts);
                resProducts.push(reaction.product);
            }
            productCounts[reaction.product.marts] = (productCounts[reaction.product.marts] || 0) + 1;
        });
    });
    resSubstrates.forEach(substrate => {
        substrate.countInReactions = substrateCounts[substrate.marts];
    });
    resProducts.forEach(product => {
        product.countInReactions = productCounts[product.marts];
    });

    resSubstrates.sort((a, b) => b.countInReactions - a.countInReactions);
    resProducts.sort((a, b) => b.countInReactions - a.countInReactions);

    return { 
        products: resProducts, 
        substrates: resSubstrates, 
        productCounts, 
        substrateCounts 
    };
}


function getUniqueMoleculesFromEnzymesAllInfo(enzymesData) {
    const res = {};

    enzymesData.forEach(enzyme => {
        enzyme.reactions.forEach(reaction => {
            reaction.substrates.forEach(substrate => {
                if (!res[substrate.marts]) {
                    res[substrate.marts] = {
                        ...substrate,
                        countInReactions: 1,
                        countAsSubstrate: 1,
                        countAsProduct: 0,
                        kingdoms: new Set([enzyme.kingdom]),
                        types: new Set([reaction.type]),
                        kingdomCounts: {[enzyme.kingdom]: 1},
                    };
                } else {
                    res[substrate.marts].countAsSubstrate += 1;
                    res[substrate.marts].countInReactions += 1;
                    res[substrate.marts].kingdoms.add(enzyme.kingdom);
                    res[substrate.marts].types.add(reaction.type);
                    res[substrate.marts].kingdomCounts[enzyme.kingdom] = 
                        (res[substrate.marts].kingdomCounts[enzyme.kingdom] || 0) + 1;
                }
            });
            if (!res[reaction.product.marts]) {
                res[reaction.product.marts] = {
                    ...reaction.product,
                    countInReactions: 1,
                    countAsSubstrate: 0,
                    countAsProduct: 1,
                    kingdoms: new Set([enzyme.kingdom]),
                    types: new Set([reaction.type]),
                    kingdomCounts: {[enzyme.kingdom]: 1},
                };
            } else {
                res[reaction.product.marts].countAsProduct += 1;
                res[reaction.product.marts].countInReactions += 1;
                res[reaction.product.marts].kingdoms.add(enzyme.kingdom);
                res[reaction.product.marts].types.add(reaction.type);
                res[reaction.product.marts].kingdomCounts[enzyme.kingdom] = 
                    (res[reaction.product.marts].kingdomCounts[enzyme.kingdom] || 0) + 1;
            }
        });
    });

    let res_vals = Object.values(res);
    res_vals.sort((a, b) => b.countInReactions - a.countInReactions);
    console.log("getUniqueMoleculesFromEnzymesAllInfo", res_vals);
    return { 
        molecules: res_vals 
    };
}


// --- Product-yield ranking ---------------------------------------------------
// MARTS stores the product yield as a free-text field. Per the marts-db data
// dictionary the precedence is: major > second > any number > minor > trace.
// Missing, empty, "?" or unrecognised yields rank as minor. yieldRankScore maps
// a raw yield to a sortable number so competing products can be compared. Tiers
// are spaced by 1e6; numeric yields keep their value as a tie-breaker so e.g.
// "70%" outranks "20%" within the numeric tier.
function yieldRankScore(rawYield) {
    if (rawYield === null || rawYield === undefined) return 1e6; // minor
    const s = rawYield.toString().trim().toLowerCase();
    if (s === "" || s === "?") return 1e6;                       // minor
    if (s === "major") return 4e6;
    if (s === "second") return 3e6;
    if (s === "minor") return 1e6;
    if (s === "trace") return 0;
    const match = s.match(/[\d.]+/);                             // "70%", "less 5%", "2:1"
    if (match) {
        const num = parseFloat(match[0]);
        if (!isNaN(num)) return 2e6 + Math.min(num, 999999);
    }
    return 1e6;                                                  // unrecognised -> minor
}

// Does a single yield carry positive evidence that a product can be a major
// one — i.e. a numeric (percentage/ratio) value or the literal "major"? A
// "minor"/"trace"/"second"/missing/"?"/unrecognised yield does NOT qualify.
// Mirrors the branches of yieldRankScore so the two stay in agreement.
function isMajorDefiningYield(rawYield) {
    if (rawYield === null || rawYield === undefined) return false;
    const s = rawYield.toString().trim().toLowerCase();
    if (s === "" || s === "?") return false;
    if (s === "major") return true;
    if (s === "second" || s === "minor" || s === "trace") return false;
    const match = s.match(/[\d.]+/);
    return !!match && !isNaN(parseFloat(match[0]));
}

// Is `rawYield` (one product) a major product within its comparison group?
// `groupYields` is the list of yields of all competing products (same enzyme,
// same terpene type) and is expected to include rawYield itself.
//
// A lone product of its type is always major, regardless of how (or whether)
// its yield is recorded. Beyond that, calling something "major" requires
// positive evidence somewhere in the group: at least one product with a
// numeric yield or an explicit "major". If the group holds only
// minor/trace/"second"/missing yields there is no major product — every
// product is minor (so an all-"minor" or all-missing group yields no major,
// rather than the whole group tying at the top and all counting as major).
// When such evidence exists, the product is major when nothing in the group
// ranks strictly above it, so ties at the top all count as major.
function isMajorYield(rawYield, groupYields) {
    if (groupYields.length <= 1) return true;
    if (!groupYields.some(isMajorDefiningYield)) return false;
    const score = yieldRankScore(rawYield);
    const maxScore = groupYields.reduce((m, y) => Math.max(m, yieldRankScore(y)), 0);
    return score >= maxScore;
}

// Split the enzymes shown on a molecule record into the four roles the molecule
// can play for an enzyme: major producer, minor producer, substrate, or
// (mechanism) intermediate. Each enzyme is assigned to exactly one bucket by
// priority (major producer > minor producer > substrate > intermediate) so no
// enzyme card is rendered more than once. Whether a produced molecule is "major"
// is decided per terpene type: it is compared only against the enzyme's other
// products of the same type (see isMajorYield). `enzymes` is the grouped output
// of getDataByEnzymesFromSearchResults; each enzyme's full reaction set is used.
function splitEnzymesByMoleculeRole(enzymes, moleculeMarts) {
    const majorProducers = [];
    const minorProducers = [];
    const substrateUsers = [];
    const intermediates = [];

    enzymes.forEach(enzyme => {
        // Yields of every product this enzyme makes, grouped by terpene type.
        const yieldsByType = {};
        enzyme.reactions.forEach(r => {
            (yieldsByType[r.type] = yieldsByType[r.type] || []).push(r.product_yield);
        });

        const producingReactions = enzyme.reactions.filter(r => r.product.marts === moleculeMarts);
        const usesAsSubstrate = enzyme.reactions.some(
            r => r.substrates.some(s => s.marts === moleculeMarts)
        );

        if (producingReactions.length > 0) {
            const isMajor = producingReactions.some(
                r => isMajorYield(r.product_yield, yieldsByType[r.type])
            );
            (isMajor ? majorProducers : minorProducers).push(enzyme);
        } else if (usesAsSubstrate) {
            substrateUsers.push(enzyme);
        } else {
            // Touches the molecule only as a mechanism intermediate.
            intermediates.push(enzyme);
        }
    });

    return { majorProducers, minorProducers, substrateUsers, intermediates };
}

// A molecule filter row in the browse/search forms is only meaningful once it
// resolves to a real MARTS molecule: MoleculeForm auto-fills `marts` (via
// /submitcheck) only when the typed SMILES/name/ChEBI actually exists in MARTS.
// Returns true when any row has a typed identifier that never resolved, meaning
// the filter cannot match anything and the search should display nothing rather
// than silently dropping the filter (which would return everything).
function hasUnresolvedMolecule(molecules) {
    return (molecules || []).some(mol => {
        const typedIdentifier = ["smiles", "name", "chebi"].some(
            f => mol[f] && mol[f].trim() !== ""
        );
        const resolved = mol.marts && mol.marts.trim() !== "";
        return typedIdentifier && !resolved;
    });
}

export {getUniqueMoleculesFromEnzyme}
export {getUniqueMoleculesFromEnzymesAllInfo};
export {yieldRankScore, isMajorYield, splitEnzymesByMoleculeRole};
export {hasUnresolvedMolecule};